/**
 * The price column: what this build costs, and why.
 *
 * Every row is an AUTHORED signed delta against `Base` (= products.base_price),
 * so the column sums to the headline by construction rather than by a second
 * calculation that can drift from the first. `total` arrives already computed
 * by the island — this component never does arithmetic on money, which is the
 * point: there is exactly one place a price is derived, and it is also the
 * number handed to the cart.
 *
 * Shipping is quoted BESIDE the total, never folded into it. $300/set is real
 * freight charged at checkout, and rolling it in would make /build disagree
 * with both the PDP and the cart for the same board.
 *
 * No `bg-*` on the card. `--color-surface-muted` is a 10%-alpha wash, not a
 * fill, and `--color-surface-card` is derived at `:root` from a variable the
 * layout sets on `<body>` — so it silently resolves to the platform default on
 * every tenant. A border on the page canvas is honest and costs nothing.
 */

import { SET_SHIPPING_RATE } from '@sifly/shared-utils'
import { money } from '../_lib/money'

const RADIUS = 'rounded-[var(--layout-button-radius,var(--brand-radius,0.5rem))]'
const ROW = 'flex items-baseline justify-between gap-4'

/** Not exported: the island is the only caller, and knip fails an export with
 *  no importer. */
type SummaryLine = { id: string; slot: string; option: string; delta: number }

function signed(delta: number): string {
  if (delta === 0) return 'Included'
  return `${delta > 0 ? '+' : '−'}${money(Math.abs(delta))}`
}

export function BuildSummary({
  basePrice,
  lines,
  savings,
  total,
  ready,
  added,
  onAdd,
}: {
  basePrice: number
  /** One row per chosen option, in slot order. */
  lines: SummaryLine[]
  /** Combo rules the current configuration actually earned. */
  savings: { id: string; label: string; amount: number }[]
  /** `basePrice + delta`, already net of `savings`. The exact figure charged. */
  total: number
  ready: boolean
  added: boolean
  onAdd: () => void
}) {
  return (
    <div className={`border border-[var(--color-border)] ${RADIUS} p-5`}>
      <h2 className="text-sm font-medium text-[var(--color-text-primary)]">Your build</h2>

      <dl className="mt-3 space-y-2 text-sm">
        <div className={ROW}>
          <dt className="text-[var(--color-text-secondary)]">Base</dt>
          <dd className="tabular-nums text-[var(--color-text-primary)]">{money(basePrice)}</dd>
        </div>

        {lines.map((line) => (
          <div key={line.id} className={ROW}>
            <dt className="text-[var(--color-text-secondary)]">
              {line.slot}
              <span className="block text-xs">{line.option}</span>
            </dt>
            <dd className="shrink-0 tabular-nums text-[var(--color-text-primary)]">{signed(line.delta)}</dd>
          </div>
        ))}

        {/* Earned combo savings. Calm and factual — a quiet confirmation that
            the lower total is intentional, not a sales shout. The border is
            load-bearing: the muted token is a 10% wash, so without it the row
            has no edge and reads as a stray line of text. */}
        {savings.map((saving) => (
          <div
            key={saving.id}
            className={`${ROW} ${RADIUS} border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3 py-2`}
          >
            <dt className="text-[var(--color-text-secondary)]">{saving.label}</dt>
            <dd className="shrink-0 tabular-nums font-medium text-[var(--color-text-primary)]">
              −{money(saving.amount)}
            </dd>
          </div>
        ))}

        <div className={`${ROW} border-t border-[var(--color-border)] pt-3`}>
          <dt className="font-medium text-[var(--color-text-primary)]">Total</dt>
          <dd className="text-lg font-semibold tabular-nums text-[var(--color-text-primary)]">
            {money(total)}
          </dd>
        </div>
      </dl>

      <button
        type="button"
        onClick={onAdd}
        disabled={!ready}
        aria-disabled={!ready}
        className={`cta-primary mt-4 w-full py-3 ${RADIUS} font-medium transition-opacity hover:opacity-90 disabled:opacity-50`}
      >
        {added ? 'Added to cart!' : 'Add to cart'}
      </button>

      <p className="mt-2 text-xs text-[var(--color-text-secondary)]">
        Shipping {money(SET_SHIPPING_RATE)} per set, calculated at checkout
      </p>
    </div>
  )
}
